import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  demandsAPI,
  type DemandListFilters,
  type DistributionFilters,
  type OverviewFilters,
  type UpsertSLAInput,
} from '../api/demands.api';

export function useDemands(filters: DemandListFilters = {}) {
  return useQuery({
    queryKey: ['demands', 'list', filters],
    queryFn: () => demandsAPI.list(filters),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useActiveDemands() {
  return useQuery({
    queryKey: ['demands', 'active'],
    queryFn: () => demandsAPI.listActive(),
    // Atualiza da API a cada 30s para captar mudanças de status vindas do Notion.
    // O cronômetro visual continua rodando localmente entre os refetches.
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useResponsibleMetrics(filters: { from?: string; to?: string } = {}) {
  return useQuery({
    queryKey: ['demands', 'metrics', 'by-responsible', filters],
    queryFn: () => demandsAPI.metricsByResponsible(filters),
    staleTime: 60 * 1000,
  });
}

export function useTypeMetrics(filters: { from?: string; to?: string } = {}) {
  return useQuery({
    queryKey: ['demands', 'metrics', 'by-type', filters],
    queryFn: () => demandsAPI.metricsByType(filters),
    staleTime: 60 * 1000,
  });
}

export function useWorkload(filters: { stuckHours?: number } = {}) {
  return useQuery({
    queryKey: ['demands', 'metrics', 'workload', filters],
    queryFn: () => demandsAPI.workload(filters),
    // Reflete carga em tempo quase real, mesmo intervalo do overview.
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useThroughput(filters: { from?: string; to?: string } = {}) {
  return useQuery({
    queryKey: ['demands', 'metrics', 'throughput', filters],
    queryFn: () => demandsAPI.throughput(filters),
    staleTime: 60 * 1000,
  });
}

export function useDemandsOverview(filters: OverviewFilters = {}) {
  return useQuery({
    queryKey: ['demands', 'metrics', 'overview', filters],
    queryFn: () => demandsAPI.overview(filters),
    // KPIs precisam refletir rapidamente ciclos abertos / travados, mas não
    // tão agressivos quanto a listagem de ativos. 30s de refetch é um bom
    // equilíbrio entre "sensação de tempo real" e carga no backend.
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useDemandDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['demands', 'detail', id],
    queryFn: () => demandsAPI.getById(id!),
    enabled: !!id,
  });
}

export function useDemandsFacets() {
  return useQuery({
    queryKey: ['demands', 'meta', 'facets'],
    queryFn: () => demandsAPI.facets(),
    // Facets mudam pouco (só quando aparece um novo tipo/status/responsável);
    // 5 min de stale é ok.
    staleTime: 5 * 60 * 1000,
  });
}

export function useDistribution(filters: DistributionFilters = {}) {
  return useQuery({
    queryKey: ['demands', 'metrics', 'distribution', filters],
    queryFn: () => demandsAPI.distribution(filters),
    staleTime: 60 * 1000,
  });
}

export function useSLAs() {
  return useQuery({
    queryKey: ['demands', 'sla', 'list'],
    queryFn: () => demandsAPI.listSLAs(),
    staleTime: 60 * 1000,
  });
}

export function useUpsertSLA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertSLAInput) => demandsAPI.upsertSLA(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['demands', 'sla'] });
      // Overview usa SLAs, então precisa refetch
      qc.invalidateQueries({ queryKey: ['demands', 'metrics', 'overview'] });
    },
  });
}

export function useDeleteSLA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => demandsAPI.deleteSLA(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['demands', 'sla'] });
      qc.invalidateQueries({ queryKey: ['demands', 'metrics', 'overview'] });
    },
  });
}
